/**
 * Carousel of lifestyle media in the PDP additional content column.
 *
 * Slide widths come from CSS — each slide is a fraction of the carousel width
 * so every slide renders at the height of the widest one — which is why this
 * runs with `slidesPerView: "auto"`.
 */

import { openZoom } from "./lifestyle-media";

// Matches the gap between slides in the design.
const SPACE_BETWEEN = 24;

// How much of the carousel has to reach the screen before its slides count as
// seen — an edge of it scrolling past the fold doesn't.
const SEEN_THRESHOLD = 0.25;

const show = (slide) => {
  slide.removeAttribute("aria-hidden");
  slide.querySelectorAll("a, button, input").forEach((el) => {
    el.tabIndex = null;
  });
};

const hide = (slide) => {
  slide.setAttribute("aria-hidden", "true");
  slide.querySelectorAll("a, button, input").forEach((el) => {
    el.tabIndex = -1;
  });
};

class LifestyleCarousel extends HTMLElement {
  constructor() {
    super();

    this.visibleSlides = new Set();
    this.seen = false;

    window.loadSwiper.then((swiperLib) => {
      this.swiperLib = swiperLib;
      this._initCarousel();
    });
  }

  connectedCallback() {
    if (this.seen) return;

    // Unlike the main gallery, this carousel sits below the fold, so its slides
    // aren't reported until the carousel itself has been scrolled to.
    this.seenObserver = new IntersectionObserver(
      ([entry]) => {
        if (entry.intersectionRatio < SEEN_THRESHOLD) return;

        this.seen = true;
        this.seenObserver.disconnect();
        this._trackSlideViews();
      },
      { threshold: SEEN_THRESHOLD },
    );

    this.seenObserver.observe(this);
  }

  disconnectedCallback() {
    this.seenObserver?.disconnect();
    this._destroyCarousel();
  }

  _initCarousel() {
    if (this.swiper) return;

    const { Swiper } = this.swiperLib;

    this._toggleSwiperClasses(true);

    this.swiper = new Swiper(this.querySelector("[data-viewport]"), {
      slidesPerView: "auto",
      spaceBetween: SPACE_BETWEEN,
      speed: 400,
      // Needed for the `swiper-slide-visible` class the slide sync relies on.
      watchSlidesProgress: true,
      navigation: {
        nextEl: this.querySelector("[data-navigation-next]"),
        prevEl: this.querySelector("[data-navigation-previous]"),
      },
      on: {
        // Swiper only emits `click` for a press that wasn't a drag, so swiping
        // the carousel can't be mistaken for a click on the media it lands on.
        click: (swiper, e) =>
          openZoom(
            swiper.clickedSlide?.querySelector("[data-media]"),
            e.target,
          ),
        slideChangeTransitionStart: () => {
          // Nothing is settled mid-transition, so keep every slide out of
          // reach until they land.
          this.swiper.slides.forEach(hide);
        },
        slideChangeTransitionEnd: () => {
          window.requestAnimationFrame(() => this._syncSlides());
        },
        // Slide widths are a fraction of the carousel width, so how many of
        // them fit changes with the column.
        resize: () => this._syncSlides(),
      },
    });

    this._disconnectPips = window.connectCarouselPips(
      this.swiper,
      this.querySelector("carousel-pips"),
    );

    // `on.init` fires from inside the constructor, before `this.swiper` is
    // assigned — the first pass runs here instead.
    this._syncSlides();
  }

  _destroyCarousel() {
    if (!this.swiper) return;

    this._disconnectPips();
    this.swiper.destroy(true, true);
    this.swiper = null;
    this._toggleSwiperClasses(false);
  }

  /**
   * Swiper's own classes are applied at runtime so the markup still lays out as
   * a scrollable row before the library has loaded.
   */
  _toggleSwiperClasses(add) {
    this.querySelectorAll("[data-swiper-class]").forEach((el) => {
      el.classList.toggle(el.dataset.swiperClass, add);
    });
  }

  /**
   * Brings the slides in view into the tab order and plays their videos, and
   * makes everything off screen inert and paused. More than one slide can be
   * in view at a time when the media is narrower than the carousel.
   */
  _syncSlides() {
    if (!this.swiper) return;

    this.swiper.slides.forEach((slide) => {
      const isVisible = slide.classList.contains("swiper-slide-visible");

      isVisible ? show(slide) : hide(slide);

      const video = slide.querySelector("video");
      if (!video) return;

      if (isVisible) {
        video.play().catch(() => {});
      } else {
        video.pause();
      }
    });

    this._trackSlideViews();
  }

  /**
   * Reports every slide that has come into view since the last sync. Slides
   * leaving view are dropped from the set, so flipping back to one counts as a
   * fresh view.
   *
   * Nothing is recorded before the carousel has been scrolled to — the set has
   * to still be empty at that point for the slides on screen to read as newly
   * visible.
   *
   * Only ever called with the carousel at rest — Swiper keeps
   * `swiper-slide-visible` in step with the translate, so sampling mid
   * transition would report each slide scrolled past.
   */
  _trackSlideViews() {
    if (!this.seen || !this.swiper) return;

    this.swiper.slides.forEach((slide, index) => {
      const isVisible = slide.classList.contains("swiper-slide-visible");

      if (isVisible === this.visibleSlides.has(index)) return;

      if (!isVisible) {
        this.visibleSlides.delete(index);
        return;
      }

      this.visibleSlides.add(index);

      window.ElevarDataLayer?.push({
        event: "view_lifestyle_image",
        detail: {
          image_number: index + 1,
        },
      });
    });
  }
}

if (!customElements.get("lifestyle-carousel")) {
  customElements.define("lifestyle-carousel", LifestyleCarousel);
}
